import React, { useState } from "react"
import { useTranslation } from "react-i18next"

import { INSTANCE } from "config"
import { useAppDispatch } from "redux/hooks"
import { showAlert } from "redux/features/notice/noticeSlice"
import { makeSubscriptionPayment } from "redux/features/subscription_payment/subscriptionPaymentThunk"
import {
  Container,
  Content,
  Title,
  Description,
} from "components/Subscription/Modal/Create/elements"
import Spinner from "components/ui/Spinner"
import Form from "components/Subscription/Modal/Create/BankPayment/Form"
import BankLinkForm from "components/Subscription/Modal/Create/BankPayment/BankLinkForm"
import {
  SubscriptionPaymentFormValues,
  SubscriptionPaymentMethodList,
} from "types"

type Props = {
  subscriptionId: string
  paymentMethods: SubscriptionPaymentMethodList
}

const PAYMENT_REQUEST_DEFAULT = {
  VK_SERVICE: "1012",
  VK_VERSION: "008",
  VK_SND_ID: "",
  VK_STAMP: "",
  VK_AMOUNT: "",
  VK_CURR: "EUR",
  VK_REF: "",
  VK_MSG: "",
  VK_RETURN: "",
  VK_CANCEL: "",
  VK_DATETIME: "",
  VK_MAC: "",
  VK_ENCODING: "UTF-8",
  VK_LANG: "ENG",
}

const BankPayment = ({ subscriptionId, paymentMethods }: Props) => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const [paymentRequestUri, setPaymentRequestUri] = useState("")
  const [paymentRequestMethod, setPaymentRequestMethod] = useState("POST")
  const [paymentRequestParams, setPaymentRequestParams] = useState(
    PAYMENT_REQUEST_DEFAULT,
  )
  const [isSubmitting, setSubmitting] = useState(false)

  const handleSubmit = (values: SubscriptionPaymentFormValues) => {
    const instance = INSTANCE
    setSubmitting(true)

    const params = {
      subscription_id: subscriptionId,
      instance,
      ...values,
    }

    dispatch(makeSubscriptionPayment(params))
      .then(({ request_uri, request_method, request_params }) => {
        setPaymentRequestMethod(request_method || "")
        setPaymentRequestParams(request_params || PAYMENT_REQUEST_DEFAULT)
        setPaymentRequestUri(request_uri || "")
      })
      .catch((error) => {
        if (typeof error === "string") {
          dispatch(
            showAlert({
              message: error,
            }),
          )
        }
      })
      .finally(() => {
        setSubmitting(false)
      })
  }

  return (
    <Content>
      <Spinner open={isSubmitting} />
      <Title variant="h4">{t("features.subscription.steps.bank.title")}</Title>
      <Description variant="h6">
        {t("features.subscription.steps.bank.description")}
      </Description>
      <Container
        container
        direction="row"
        justifyContent="center"
        alignItems="center"
      >
        {paymentMethods.map(({ color, label, value }, index: number) => (
          <Form
            key={index}
            initialValues={{
              bank_name: value,
            }}
            disabled={isSubmitting}
            title={t(`banks.${label}.title`)}
            color={color}
            onSubmit={handleSubmit}
          />
        ))}
        {paymentRequestUri && (
          <BankLinkForm
            uri={paymentRequestUri}
            method={paymentRequestMethod}
            params={paymentRequestParams}
          />
        )}
      </Container>
    </Content>
  )
}

export default BankPayment
